/**
 * AMD-37 — the wave arc. The AMD-34 wave beat (`onset | sustain | lull | muster`) drives
 * the §12.5 three-layer music mix, and the `muster` beat schedules a telegraph that lands
 * on the wave boundary.
 *
 * Everything here is pure: no WebAudio, no `World`, no clock. `src/audio/index.ts` reads
 * the segment off the sim and turns these numbers into AudioParam automation; `sfx.ts`
 * reads {@link musterPulseOffsets} so the recipe and the plan cannot drift apart.
 *
 * How this composes with §12's phase rules (the judgement call AMD-37 records):
 * the phase sets the register — its §8.2 per-minute budget is the `pressure` factor
 * below, and a `phase_change` still crossfades over 2 s (AMD-4). The wave arc is a
 * multiplier *inside* that register, never an override. A boss floor (§12.5, I ≥ 0.8)
 * outranks both: a boss fight has no audible lull.
 */

export type WaveSegment = 'onset' | 'sustain' | 'lull' | 'muster';

export const WAVE_SEGMENTS: readonly WaveSegment[] = ['onset', 'sustain', 'lull', 'muster'];

/**
 * Wave shape S, applied to the phase pressure P as `I = clamp01(P · S)`.
 *
 * Strictly monotone `onset > sustain > muster > lull` so the sawtooth survives even at
 * P = 1, where a raw §8.2 intensity would clip onset and sustain onto the same value.
 * The lull number is the load-bearing one. Measured over waves 14–17, the §12.5 layer
 * formula takes L3 (lead + kick + clap + hats) from 0.18 in the fight to 0.06 in the lull
 * and lifts L1 (pad + drone) from 0.79 to 0.97, while the R-DRONE and arp cutoffs track I
 * from ~2.4 kHz down to ~960 Hz. Percussion and lead leave, the drone stays and darkens.
 * That is what makes a lull read as breathing space instead of a dropout bug.
 */
export const SEGMENT_MIX: Readonly<Record<WaveSegment, number>> = {
  onset: 1.0,
  sustain: 0.85,
  lull: 0.28,
  muster: 0.4,
};

/**
 * Ramp into each beat. The onset slams (0.35 s), the lull lets the air out slowly
 * (1.8 s) so the drop reads as relief rather than a cut, and `muster` is overridden at
 * the call site with the beat's own `remaining_s` so its swell lands on the boundary.
 */
export const SEGMENT_RAMP_S: Readonly<Record<WaveSegment, number>> = {
  onset: 0.35,
  sustain: 1.2,
  lull: 1.8,
  muster: 3.0,
};

/**
 * Level trim on the music sub-mix, under the §12.5 crossfade.
 *
 * §12.5's crossfade is equal-power by construction (`layerGains` normalizes to unit
 * power), so on its own it changes *what* you hear without changing how loud it is —
 * measured across a wave it produced a 1.4 dB master swing, which is a change of
 * instrumentation, not relief. The trim is the other half: a real duck through the lull
 * and a real swell into the onset, applied below the crossfade so the two compose.
 *
 * Nothing here ever exceeds 1.0, so the §12.6 headroom rule is untouched: the loudest
 * the music sub-mix can be after AMD-37 is exactly as loud as it was before it.
 */
export const SEGMENT_LEVEL: Readonly<Record<WaveSegment, number>> = {
  onset: 1.0, //     0 dB
  sustain: 0.891, // −1 dB
  muster: 0.562, //  −5 dB
  lull: 0.398, //    −8 dB
};

/** Inside a boss fight (§12.5's I ≥ 0.8 floor) the trim never dips below the fight level. */
export function musicLevel(segment: WaveSegment, bossFloored: boolean): number {
  const l = SEGMENT_LEVEL[segment];
  if (!bossFloored) return l;
  return l < SEGMENT_LEVEL.sustain ? SEGMENT_LEVEL.sustain : l;
}

/** §12.5 — `I = clamp01(current-minute threat budget / 1200)`. */
export const PRESSURE_DIVISOR = 1200;

/**
 * Floor under the §12.5 pressure. Minute 0's budget is a fraction of 1200, and without a
 * floor the first waves would modulate a mix that is already silent — the arc would only
 * become audible around minute 6. The floor costs nothing at high pressure.
 */
export const MIN_PRESSURE = 0.22;

export function clamp01(x: number): number {
  if (!(x > 0)) return 0;
  return x > 1 ? 1 : x;
}

/**
 * §12.5 intensity for the *phase*, before the wave shape: the minute's §8.2 budget,
 * scaled by the §8.9 recovery envelope so a post-boss recovery still reads as one.
 */
export function wavePressure(minuteBudget: number, recoveryEnvelope: number): number {
  const p = clamp01(minuteBudget / PRESSURE_DIVISOR) * clamp01(recoveryEnvelope);
  return p < MIN_PRESSURE ? MIN_PRESSURE : p;
}

/** Final music intensity I for a beat. `floor` is the §12.5 boss floor (0.8 in a fight). */
export function waveMixTarget(segment: WaveSegment, pressure: number, floor = 0): number {
  const i = clamp01(SEGMENT_MIX[segment] * clamp01(pressure));
  const f = clamp01(floor);
  return i < f ? f : i;
}

/** Ramp seconds for a beat. `muster` rides its own countdown so it lands on the boundary. */
export function segmentRampS(segment: WaveSegment, remainingS: number): number {
  if (segment !== 'muster') return SEGMENT_RAMP_S[segment];
  return remainingS > 0.05 ? remainingS : 0.05;
}

// ---------------------------------------------------------------------------
// The muster telegraph
// ---------------------------------------------------------------------------

/** Longest lead the telegraph takes. AMD-34 gives `muster` exactly 3 s. */
export const MUSTER_MAX_LEAD_S = 3;

export const MUSTER_CUE = 'audio.wave.muster';
export const MUSTER_CUE_BOSS = 'audio.wave.muster-boss';
/** Wave-boundary punctuation, so the rhythm is countable with eyes on the arena. */
export const WAVE_MARK_CUE = 'audio.wave.mark';

/** A boss boundary takes the mark down a fourth and softer — the roar carries it. */
export const BOSS_MARK_PITCH = 0.75;
export const BOSS_MARK_GAIN = 0.7;

export interface MusterPlan {
  cueId: string;
  /** Seconds from now until the cue starts. */
  startInS: number;
  /** Cue length. Its swell peaks exactly `startInS + durS` from now — the boundary. */
  durS: number;
  /** Countable pulses: 2 ordinary, 3 boss. */
  pulses: number;
  boss: boolean;
}

const PLAN: MusterPlan = { cueId: MUSTER_CUE, startInS: 0, durS: 0, pulses: 0, boss: false };

/**
 * Schedules the telegraph so its peak coincides with the wave boundary, `remainingS`
 * from now. Returned object is reused — read it before the next call.
 *
 * `startInS + durS === remainingS` holds for every input, which is the whole point: the
 * cue is placed once on the AudioContext timeline instead of chased by the 10 Hz poll.
 */
export function planMuster(remainingS: number, boss: boolean): MusterPlan {
  const remaining = remainingS > 0 ? remainingS : 0;
  const lead = remaining < MUSTER_MAX_LEAD_S ? remaining : MUSTER_MAX_LEAD_S;
  PLAN.cueId = boss ? MUSTER_CUE_BOSS : MUSTER_CUE;
  PLAN.startInS = remaining - lead;
  PLAN.durS = lead;
  PLAN.pulses = boss ? 3 : 2;
  PLAN.boss = boss;
  return PLAN;
}

/**
 * Pulse onsets inside the cue, seconds from the cue's own `t0`. The last pulse lands one
 * whole second before the boundary, the one before it two seconds, and so on — a
 * countdown a player can count. Negative entries (a late bind mid-muster) are dropped.
 * Fills and returns `out`, which the recipe preallocates.
 */
export function musterPulseOffsets(durS: number, pulses: number, out: number[]): number[] {
  out.length = 0;
  for (let k = 0; k < pulses; k++) {
    const t = durS - (pulses - k);
    if (t >= 0) out.push(t);
  }
  return out;
}

/**
 * Per-wave latch. `wave_phase` fires `muster` once, but the 10 Hz poll re-derives the
 * segment from the sim as well (so a mid-wave bind still gets an arc), and both paths
 * must not double-schedule the telegraph.
 */
export class WaveArc {
  /** 1-based wave the arc last saw. */
  wave = 0;
  segment: WaveSegment = 'sustain';
  pressure = MIN_PRESSURE;
  private musterWave = -1;

  /** True the first time `muster` comes up for this wave. */
  armMuster(wave: number): boolean {
    if (this.musterWave === wave) return false;
    this.musterWave = wave;
    return true;
  }

  reset(): void {
    this.wave = 0;
    this.segment = 'sustain';
    this.pressure = MIN_PRESSURE;
    this.musterWave = -1;
  }
}
